import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, useMediaQuery, useTheme } from '@mui/material';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import DownloadIcon from '@mui/icons-material/Download';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import KeyboardDoubleArrowDownRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowDownRounded';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AnalyticsIcon from '@mui/icons-material/Analytics';

import { TooltipedActionButton, TooltipedDropdownButton } from 'src/App/Components/Buttons';
import { User } from 'src/interfaces/iuser';
import { callApi } from 'src/core/callApi';
import ComingSoonModal from 'src/App/Modals/ComingSoonModal';
import { useHistory } from 'react-router-dom';
import { handleSaveDeleteAdvancedMarket } from 'src/utils/functions';
import { logAmplitudeEvent } from 'src/utils/analytics';
import { updateMarketInList } from 'src/store/Dashboard/actions';

const ActionToolsBar = ({
  setShowListingsExportPage,
  handleOnSampleClick,
  setShowSubmarketCreationModal,
  marketID,
}) => {
  const user: User = useSelector((state: any) => state.dashboard.user);
  const search = useSelector((state: any) => state.dashboard.search);
  const savedMarketsState = useSelector((state: any) => state.dashboard.savedMarkets);
  const { data: selectedList } = savedMarketsState;

  const [subMarketOptionOpen, setSubMarketOptionOpen] = useState<boolean>(false);

  const history = useHistory();
  const dispatch = useDispatch();

  const isMarketInCurrentSavedList =
    selectedList && selectedList.marketDetails && selectedList.marketIDs[marketID];

  const handleSaveClick = async () => {
    if (isMarketInCurrentSavedList) {
      dispatch(updateMarketInList(selectedList, marketID, 'remove', ''));
    } else {
      dispatch(updateMarketInList(selectedList, marketID, 'add', ''));
    }
  };

  const handleDownloadMarketSummarySampleClick = () => {
    logAmplitudeEvent('market_summary_sample_downloaded');
    const downloadUrl =
      'https://airbtics-resource.s3.us-east-2.amazonaws.com/Airbtics-Sample-Market-Summary.csv';
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', 'Airbtics-Sample-Market-Summary.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubscribeToScaleClick = () => {
    handleOnSampleClick('scale');
  };

  const handleDownloadListingLevelClick = () => {
    logAmplitudeEvent('listing_export_modal_opened');
    setShowListingsExportPage(true);
  };

  const handleCreatSubmarketClick = () => {
    setShowSubmarketCreationModal(true);
  };

  const downloadOptions = [
    { label: 'Download Sample', onClick: handleDownloadMarketSummarySampleClick },
    { label: 'Subscribe to Scale', onClick: handleSubscribeToScaleClick },
  ];

  const getIfMarketSavedStatusIcon = () => {
    if (isMarketInCurrentSavedList === 'saving') return AutorenewIcon;

    if (isMarketInCurrentSavedList) return BookmarkIcon;

    return BookmarkAddOutlinedIcon;
  };

  const getIfMarketSavedStatusText = () => {
    if (isMarketInCurrentSavedList === 'saving') return 'Saving';

    if (isMarketInCurrentSavedList) return 'Saved';

    return 'Save';
  };

  const isSubmarket = search.data.isSubmarket;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.up('xs'));

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)',
          backgroundColor: '#f5f5f5',
          borderRadius: '12px',
          padding: '10px 20px',
          width: 'fit-content',
          margin: '0 auto',
          gap: '10px',
        }}
      >
        <TooltipedActionButton
          title="Save markets to track key metrics and export to Excel. Only available on Scale plan."
          onClick={handleSaveClick}
          icon={getIfMarketSavedStatusIcon()}
          label={getIfMarketSavedStatusText()}
          animation={isMarketInCurrentSavedList === 'saving' ? 'animation-loading-spin' : undefined}
        />

        <TooltipedActionButton
          title="Save markets to track key metrics and export to Excel. Only available on Scale plan."
          onClick={() => history.push('/airbnb-data/landing')}
          icon={AnalyticsIcon}
          label={'My Markets'}
          smallLabel={true}
        />

        {user.subscriptionPlan != 'scale' ? (
          <TooltipedDropdownButton
            title="Available on the Scale plan only. If you're on the Scale plan, save the market first and click here. This will open the advanced dashboard, where you can download the market summary for all your saved markets."
            icon={DownloadIcon}
            label="Market Summary"
            options={downloadOptions}
            smallLabel={true}
          />
        ) : (
          <TooltipedActionButton
            title="Available on the Scale plan only. If you're on the Scale plan, save the market first and click here. This will open the advanced dashboard, where you can download the market summary for all your saved markets."
            onClick={() => history.push('/advanced-dashboard')} // TODO: implement this
            icon={DownloadIcon}
            label="Market Summary"
            smallLabel={true}
          />
        )}

        <TooltipedActionButton
          title="Download all the data on short-term rental listings in this market. This is an add-on product. It is not included in any of the subscription plans. Purchase it now."
          onClick={handleDownloadListingLevelClick}
          icon={DownloadIcon}
          label="Listing Level"
          smallLabel={true}
        />

        {!isSubmarket && (
          <TooltipedActionButton
            title=""
            onClick={() => setSubMarketOptionOpen(true)}
            icon={KeyboardDoubleArrowDownRoundedIcon}
            label="My Submarkets"
            smallLabel={true}
          />
        )}

        {!isSubmarket && (
          <TooltipedActionButton
            title="Create a new submarket by specifying custom boundaries and filters for detailed market analysis."
            onClick={handleCreatSubmarketClick}
            icon={AddCircleOutlineIcon}
            label="Create Submarket"
            smallLabel={true}
            highlight={true}
          />
        )}
      </Box>
      {subMarketOptionOpen && <ComingSoonModal open={true} setOpen={setSubMarketOptionOpen} />}
    </>
  );
};

export default ActionToolsBar;
